import { useQueries } from '@tanstack/react-query';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { fetchParts } from '@hub/bearing-hub/BearingHubPage/parts/fetchParts';
import type {
  IbhIcnPartsRequestDto,
  IbhPartsRequestDto,
  IbhProcedure,
  IbhShape,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhIcnProcedure, IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { isBearingParamsComplete } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import getConfig from 'next/config';
import { fetchIcnParts } from '@hub/bearing-hub/BearingHubPage/parts/fetchIcnParts';
import { cncProcedures } from '@hub/bearing-hub/common/constants';
import { QueryStatusName } from '@hub/bearing-hub/common/ui/IbhQueryStatusHandler/IbhQueryStatusHandler';
import type { IbhPartsResponseDto } from '@igus/iglidur-bearing-hub-api';
import type { UnitSystem } from '@igus/kopla-domain';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

type UseBearingPartsApiProps = {
  enabled: boolean;
  shape: IbhShape;
  materials: string[];
  productionMethods: IbhProductionMethods[];
  unitSystem: UnitSystem;
  quantity: number;
  dimensions?: IbhDimensions;
  session?: Session;
  procedures: (IbhProcedure | IbhIcnProcedure)[];
  serverData: IbhPartsResponseDto[];
  filterReleaseLocales?: boolean;
};

export const useBearingPartsApi = ({
  enabled,
  shape,
  materials,
  productionMethods,
  unitSystem,
  quantity,
  dimensions,
  session,
  procedures,
  filterReleaseLocales,
  serverData,
}: UseBearingPartsApiProps) => {
  const icnProcedures = procedures?.filter(
    (procedure): procedure is IbhIcnProcedure =>
      Object.values(IbhIcnProcedure).includes(procedure as IbhIcnProcedure) &&
      cncProcedures.includes(procedure as IbhIcnProcedure)
  );
  const ibhProcedures = procedures?.filter(
    (procedure): procedure is IbhProcedure => procedure !== IbhIcnProcedure.CNCEXPRESS
  );
  const ibhProductionMethods = productionMethods?.filter(method => method !== IbhProductionMethods.SEMIFINISHED);

  const icnMaterials = [
    ...new Set(materials.map(material => (material.endsWith('-CNC') ? material : `${material}-CNC`))),
  ];
  const config = {
    ...dimensions,
    shape,
    unitSystem,
  } as IbhPartsRequestDto;

  const configComplete = isBearingParamsComplete(config);
  const canFetchIcnParts =
    (productionMethods?.includes(IbhProductionMethods.SEMIFINISHED) || productionMethods === undefined) &&
    configComplete;
  const useLifetimeCalculation = false;

  const queryKey = [
    'bearing-parts',
    quantity,
    unitSystem,
    shape,
    dimensions,
    materials,
    productionMethods,
    procedures,
    filterReleaseLocales,
    session?.sessionId,
  ].filter(Boolean);

  const icnQueryKey = ['icn-bearing-parts', ...queryKey.slice(1)];

  const requestParams = {
    shape,
    unitSystem,
    quantity,
    productionMethods,
    materials,
    dimensions,
    procedures,
    filterReleaseLocales,
  };
  const fetchPartsData = () =>
    fetchParts({ sid: session.sessionId }, {
      ...requestParams,
      procedures: ibhProcedures,
      productionMethods: ibhProductionMethods,
      materials,
      useLifetimeCalculation,
      unitSystem,
    } satisfies IbhPartsRequestDto);

  const fetchIcnPartsData = () =>
    fetchIcnParts({ sid: session.sessionId }, {
      ...requestParams,
      procedures: icnProcedures,
      materials: icnMaterials,
      dimensions: dimensions as IbhIcnPartsRequestDto['dimensions'],
      useLifetimeCalculation,
      unitSystem,
    } satisfies IbhIcnPartsRequestDto);

  const queries = [
    {
      queryKey,
      queryFn: fetchPartsData,
      enabled,
      placeholder: () => serverData,
      gcTime: publicRuntimeConfig.ibhUseQueryCacheTtl,
      retry: (failureCount: number) => failureCount <= 1 && navigator.onLine,
    },
    {
      queryKey: icnQueryKey,
      queryFn: fetchIcnPartsData,
      enabled: enabled && canFetchIcnParts,
      gcTime: publicRuntimeConfig.ibhUseQueryCacheTtl,
      retry: false,
    },
  ];

  return useQueries({
    queries,
    combine: results => {
      const combined = results.filter(r => r.data).flatMap(r => r.data as IbhPartsResponseDto[]);

      return {
        data: enabled ? combined : serverData,
        pending: results.some(r => r.isPending),
        isFetching: results.every(r => r.isFetching),
        isLoading: results.some(r => r.isFetching),
        allDoneCount: results.filter((r, index) => {
          const isEnabled = index === 0 ? enabled : enabled;
          return isEnabled && !r.isFetching && r.status !== 'pending';
        }).length,
        status: enabled && combined.length ? QueryStatusName.success : QueryStatusName.idle,
      };
    },
  });
};
